import React, { useEffect, useState } from 'react'
import { createStructuredSelector } from 'reselect'
import { connect } from 'react-redux'
import { AuthorisedUser, CrudActions, SpreadsheetMapping, SpreadsheetMappingType } from 'api'
import { Locale, Translation } from 'locale'
import { translate } from 'locale/Locale'
import { noop } from 'lodash'
import { useDispatch } from 'react-redux'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { iconSet } from 'view/components/icons'
import { Groupbox, ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import { ErrorBar } from './parseResultPanelStyles'
import { SpreadsheetMappingUploadDialogContainer, SpreadsheetMappingUploadDialogUploadPane } from './SpreadsheetMappingUploadDialogStyles'
import { UploadDropzoneFull, UploadDropzoneEmpty } from './UploadDropzone'
import { spreadsheetMappingSchema } from './schema'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { cloneMapping } from './model'

enum DialogState {
  INITIAL,
  READY,
  ERROR
}

interface ComponentState {
  dialogState: DialogState
  uploadFile?: File
  fileData?: SpreadsheetMapping
}

interface ConnectedProps {
  authorisedUser: AuthorisedUser
}

interface SpreadsheetMappingUploadDialogComponentProps {
  spreadsheetMappingType: SpreadsheetMappingType
  onClose?: () => void
}

const mapStateToProps = createStructuredSelector({
  authorisedUser: createAuthorisedUserSelector()
})

export type SpreadsheetMappingUploadDialogProps = SpreadsheetMappingUploadDialogComponentProps & ConnectedProps

export const SpreadsheetMappingUploadDialogComponent: React.FC<SpreadsheetMappingUploadDialogProps> = ({
  spreadsheetMappingType,
  authorisedUser,
  onClose = noop
}) => {
  const [{ dialogState, uploadFile, fileData }, setState] = useState<ComponentState>({
    dialogState: DialogState.INITIAL
  })
  const dispatch = useDispatch()
  const hasError = dialogState === DialogState.ERROR
  const canUpload = dialogState === DialogState.READY
  const Validator = require('jsonschema').Validator

  const getExtension = (filename: string) => filename.split('.').pop()

  const validateJson = (file: File) => getExtension(file.name) === 'json'

  const validateMapping = async (file: File) => {
    const validator = new Validator()
    const data = JSON.parse(await file.text())
    const isValidMapping = validator.validate(data, spreadsheetMappingSchema).valid
    const isCorrectMappingType = data.type === spreadsheetMappingType

    if (isValidMapping && isCorrectMappingType) {
      setState(s => ({ ...s, fileData: cloneMapping(data, authorisedUser.id), dialogState: DialogState.READY }))
    } else {
      setState(s => ({ ...s, dialogState: DialogState.ERROR }))
    }
  }

  const handleDropFiles = (files: File[]) => setState(s => ({ ...s, uploadFile: files[0] }))

  useEffect(() => {
    if (uploadFile) {
      validateJson(uploadFile) ? validateMapping(uploadFile) : setState(s => ({ ...s, dialogState: DialogState.ERROR }))
    }
  }, [uploadFile])

  const handleUpload = () => {
    if (fileData) {
      dispatch(CrudActions.spreadsheetMappingUpsert(fileData))
    }
    if (onClose) {
      onClose(fileData)
    }
  }

  return (
    <ModalWindow title={translate(Translation.TC_UPLOAD_SPREADSHEET_MAPPING)} shadow={true}>
      <ModalContent>
        <SpreadsheetMappingUploadDialogContainer>
          <SpreadsheetMappingUploadDialogUploadPane>
            <Groupbox>
              {canUpload ? (
                <UploadDropzoneFull uploadFile={uploadFile} handleDropFiles={handleDropFiles} accept={'.json'} />
              ) : (
                <UploadDropzoneEmpty
                  narrative={Locale.translate(Translation.SC_UPLOAD_SPREADSHEET_MAPPING_DROPZONE_NARRATIVE)}
                  buttonDataTestId={'SpreadsheetMappingUploadDialog-Upload-ButtonBrowse'}
                  handleDropFiles={handleDropFiles}
                  accept={'.json'}
                />
              )}
              {hasError ? (
                <ErrorBar>
                  {iconSet.warningIcon}
                  {translate(Translation.SC_UPLOAD_ERROR_SPREADSHEET_MAPPING)}
                </ErrorBar>
              ) : null}
            </Groupbox>
          </SpreadsheetMappingUploadDialogUploadPane>
        </SpreadsheetMappingUploadDialogContainer>
      </ModalContent>

      <ModalFooter>
        <TextButton dataTestId={'SpreadsheetMappingUploadDialog-Upload-ButtonCancel'} type={TextButtonType.Tertiary} onClick={onClose}>
          {Locale.translate(Translation.TC_BUTTON_CANCEL)}
        </TextButton>
        <TextButton
          dataTestId={'SpreadsheetMappingUploadDialog-Upload-ButtonComplete'}
          type={TextButtonType.Primary}
          disabled={!canUpload}
          onClick={handleUpload}>
          {Locale.translate(Translation.TC_BUTTON_UPLOAD)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}

export const SpreadsheetMappingUploadDialog = connect(mapStateToProps)(SpreadsheetMappingUploadDialogComponent)
